import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Palette, MessageSquare, Phone, Sparkles, Plus, X, MoveHorizontal, Store, MapPin, Mail, Clock, MessageCircleQuestion } from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ChatWidget from "@/components/ChatWidget";

const PRESETS = ["#2563EB", "#059669", "#D97706", "#DB2777", "#7C3AED", "#0891B2", "#DC2626", "#161614"];

function FaqEditor({ items, onChange }) {
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const add = () => {
    if (!q.trim() || !a.trim()) return;
    onChange([...(items || []), { question: q.trim(), answer: a.trim() }]);
    setQ("");
    setA("");
  };
  return (
    <div className="space-y-2">
      <Input data-testid="input-faq-question" value={q} placeholder="Pertanyaan (mis: Apakah bisa COD?)"
        onChange={(e) => setQ(e.target.value)} />
      <Textarea data-testid="input-faq-answer" value={a} rows={2} placeholder="Jawaban otomatis (tanpa AI)..."
        onChange={(e) => setA(e.target.value)} />
      <Button type="button" variant="outline" size="sm" data-testid="btn-add-faq" onClick={add} className="w-fit">
        <Plus className="w-3.5 h-3.5 mr-1" /> Tambah FAQ
      </Button>
      {(items || []).length > 0 && (
        <div className="space-y-1.5 pt-1">
          {items.map((f, i) => (
            <div key={i} data-testid={`faq-custom-item-${i}`}
              className="rounded-lg border border-border bg-card px-3 py-2 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold truncate">{f.question}</p>
                <p className="text-[11px] text-muted-foreground line-clamp-2">{f.answer}</p>
              </div>
              <button type="button" data-testid={`btn-remove-faq-${i}`}
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="hover:text-destructive shrink-0 mt-0.5"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ConfigStudio({ business, onUpdated }) {
  const [form, setForm] = useState(business.config);
  const [name, setName] = useState(business.name);
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(business.config);
    setName(business.name);
  }, [business.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/businesses/${business.id}`, {
        name, currency: business.currency, language: business.language, config: form,
      });
      onUpdated(data);
      toast.success("Konfigurasi tersimpan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-5">
        <div className="space-y-2">
          <Label>Nama Bisnis</Label>
          <Input data-testid="input-business-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Nama Chatbot</Label>
          <Input data-testid="input-chatbot-name" value={form.chatbot_name}
            onChange={(e) => set("chatbot_name", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Warna Aksen Chatbot</Label>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map((c) => (
              <button key={c} data-testid="color-picker-accent" onClick={() => set("accent_color", c)}
                className={`w-8 h-8 rounded-lg transition-transform ${form.accent_color === c ? "ring-2 ring-offset-2 ring-foreground scale-110" : ""}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)}
              className="w-8 h-8 rounded-lg border border-border cursor-pointer bg-transparent" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Pesan Sambutan</Label>
          <Textarea data-testid="input-welcome-message" value={form.welcome_message}
            onChange={(e) => set("welcome_message", e.target.value)} rows={3} />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Nomor WhatsApp Admin</Label>
          <Input data-testid="input-admin-wa-number" value={form.admin_wa_number} placeholder="628xxxxxxxxxx"
            onChange={(e) => set("admin_wa_number", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><MoveHorizontal className="w-3.5 h-3.5" /> Posisi Icon Chatbot</Label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "bottom-left", label: "Kiri Bawah", align: "justify-start" },
              { v: "bottom-right", label: "Kanan Bawah", align: "justify-end" },
            ].map((opt) => (
              <button key={opt.v} type="button" data-testid={`position-${opt.v}`}
                onClick={() => set("position", opt.v)}
                className={`rounded-xl border p-3 text-sm font-medium transition-colors ${
                  form.position === opt.v ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary"
                }`}>
                <div className="h-10 rounded-md bg-muted mb-2 flex items-end px-1.5 pb-1.5">
                  <div className={`w-full flex ${opt.align}`}>
                    <span className="w-4 h-4 rounded-full" style={{ background: form.accent_color }} />
                  </div>
                </div>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Quick Prompts</Label>
          <div className="flex gap-2">
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Tambah saran cepat..."
              onKeyDown={(e) => { if (e.key === "Enter" && prompt.trim()) { set("quick_prompts", [...form.quick_prompts, prompt.trim()]); setPrompt(""); } }} />
            <Button variant="outline" size="icon" onClick={() => { if (prompt.trim()) { set("quick_prompts", [...form.quick_prompts, prompt.trim()]); setPrompt(""); } }}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {form.quick_prompts.map((q, i) => (
              <span key={i} className="text-xs bg-secondary rounded-full pl-3 pr-1.5 py-1 flex items-center gap-1">
                {q}
                <button onClick={() => set("quick_prompts", form.quick_prompts.filter((_, j) => j !== i))}
                  className="hover:text-destructive"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border bg-muted/30 p-4">
          <div className="flex items-center gap-2">
            <MessageCircleQuestion className="w-4 h-4 text-accent" />
            <h4 className="font-display font-semibold text-sm">FAQ & Hemat Token</h4>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2 leading-relaxed">
            Pertanyaan umum dijawab otomatis <b>tanpa memanggil AI</b> (hemat kredit). Alamat, jam operasional,
            dan nomor WhatsApp otomatis memakai data "Informasi Toko" di bawah.
          </p>
          <div className="space-y-2">
            <Label>Cara Pemesanan</Label>
            <Textarea data-testid="input-order-instructions" rows={2} value={form.order_instructions || ""}
              placeholder="Contoh: Pilih produk → klik Beli → checkout di website. Kosongkan untuk jawaban default."
              onChange={(e) => set("order_instructions", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Cara Pembayaran</Label>
            <Textarea data-testid="input-payment-instructions" rows={2} value={form.payment_instructions || ""}
              placeholder="Contoh: Transfer BCA 123456789 a.n. Toko, atau QRIS saat checkout."
              onChange={(e) => set("payment_instructions", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>FAQ Kustom (Tanya–Jawab)</Label>
            <FaqEditor items={form.faq_items || []} onChange={(v) => set("faq_items", v)} />
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border bg-muted/30 p-4">
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-accent" />
            <h4 className="font-display font-semibold text-sm">Informasi Toko</h4>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2 leading-relaxed">
            Data ini dipakai chatbot untuk menjawab pertanyaan seputar alamat, kontak, dan jam buka.
          </p>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Store className="w-3.5 h-3.5" /> Nama Toko</Label>
            <Input data-testid="input-store-name" value={form.store_name || ""} placeholder={name}
              onChange={(e) => set("store_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Alamat Lengkap</Label>
            <Textarea data-testid="input-store-address" value={form.store_address || ""} rows={2}
              placeholder="Jl. Contoh No. 123, Jakarta"
              onChange={(e) => set("store_address", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Nomor Telepon</Label>
              <Input data-testid="input-store-phone" value={form.store_phone || ""} placeholder="021-1234567"
                onChange={(e) => set("store_phone", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</Label>
              <Input data-testid="input-store-email" value={form.store_email || ""} placeholder="halo@toko.com"
                onChange={(e) => set("store_email", e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Jam Operasional</Label>
            <Input data-testid="input-store-hours" value={form.store_hours || ""}
              placeholder="Senin–Jumat 09.00–17.00, Sabtu 09.00–14.00"
              onChange={(e) => set("store_hours", e.target.value)} />
          </div>
        </div>

        <Button data-testid="btn-save-business-config" onClick={save} disabled={saving}
          className="bg-accent hover:bg-accent/90 text-white">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : "Simpan Konfigurasi"}
        </Button>
      </div>

      <div className="lg:sticky lg:top-4 h-fit space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Preview Posisi Icon</p>
          <div data-testid="position-live-preview"
            className="relative rounded-2xl border border-border bg-muted/50 h-44 overflow-hidden grain">
            <div className="flex items-center gap-1.5 px-3 h-7 border-b border-border bg-card/70">
              <span className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
              <span className="ml-2 text-[10px] text-muted-foreground font-mono truncate">{name.toLowerCase().replace(/\s+/g, "")}.com</span>
            </div>
            <div className="p-4 space-y-2">
              <div className="h-2.5 w-2/3 rounded bg-foreground/10" />
              <div className="h-2 w-1/2 rounded bg-foreground/10" />
              <div className="grid grid-cols-3 gap-2 pt-2">
                {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-md bg-foreground/5" />)}
              </div>
            </div>
            <div className={`absolute bottom-3 flex ${form.position === "bottom-left" ? "left-3" : "right-3"}`}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center pulse-ring transition-all duration-300"
                style={{ background: form.accent_color, "--pulse-color": form.accent_color + "66" }}>
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Icon akan muncul di <b>{form.position === "bottom-left" ? "kiri bawah" : "kanan bawah"}</b> pada website. Simpan untuk menerapkan ke embed code.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Live Chat Preview</p>
          <div className="rounded-2xl border border-border overflow-hidden shadow-lg h-[480px]">
            <ChatWidget key={business.id + form.accent_color} token={business.token} accentOverride={form.accent_color} />
          </div>
        </div>
      </div>
    </div>
  );
}
